import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { ref, set } from "firebase/database";
import { db } from "./firebase";
import { getNextStudentId } from "./studentIdGenerator";

const ImportExportStudents = ({ courses, activeCourse }) => {
  const [isImportExpanded, setIsImportExpanded] = useState(false);
  const [importData, setImportData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);

  // Validation function for student data
  const validateStudentData = (student, rowIndex) => {
    const errors = [];

    if (!student.name || student.name.trim() === "") {
      errors.push("Name is required");
    }

    if (!student.email || student.email.trim() === "") {
      errors.push("Email is required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(student.email)) {
      errors.push("Invalid email format");
    }

    if (student.phone && !/^[\d\s\-\+\(\)]+$/.test(student.phone)) {
      errors.push("Invalid phone number format");
    }

    if (student.parentPhone && !/^[\d\s\-\+\(\)]+$/.test(student.parentPhone)) {
      errors.push("Invalid parent phone number format");
    }

    return {
      rowIndex: rowIndex + 1, // 1-based row number for user reference
      student: student,
      errors: errors,
      isValid: errors.length === 0
    };
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result);
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);

        // Process and validate each row
        const processedData = [];
        const errors = [];

        jsonData.forEach((row, index) => {
          const student = {
            name: (row.Name || row.name || "").toString().trim(),
            email: (row.Email || row.email || "").toString().trim(),
            phone: (row.Phone || row.phone || "").toString().trim(),
            parentPhone: (row["Parent Phone"] || row.parentPhone || "").toString().trim(),
            student: true,
            role: "student"
          };

          // Only process rows that have at least name or email
          if (student.name || student.email) {
            const validation = validateStudentData(student, index);
            processedData.push(student);

            if (!validation.isValid) {
              errors.push(validation);
            }
          }
        });

        setImportData(processedData);
        setValidationErrors(errors);
        setImportResults(null);
      } catch (error) {
        alert("Error parsing Excel file: " + error.message);
        setImportData([]);
        setValidationErrors([]);
        setImportResults(null);
      }
    };
    reader.onerror = () => {
      alert("Error reading file");
      setImportData([]);
      setValidationErrors([]);
      setImportResults(null);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleAddImportedUsers = async () => {
    if (importData.length === 0) {
      alert("No valid data to import");
      return;
    }

    if (!activeCourse) {
      alert("Please select a course first");
      return;
    }

    // Filter out students with validation errors
    const validStudents = importData.filter((student, index) => {
      const validation = validateStudentData(student, index);
      return validation.isValid;
    });

    if (validStudents.length === 0) {
      alert("No valid students to import. Please fix the validation errors first.");
      return;
    }

    try {
      let firstId, lastId;
      const importErrors = [];
      const successCount = [];

      for (let i = 0; i < validStudents.length; i++) {
        try {
          const studentId = await getNextStudentId();
          const studentData = {
            ...validStudents[i],
            studentId,
            enrolledCourse: activeCourse,
            student: true,
            role: "student"
          };

          await set(ref(db, `courses/${activeCourse}/students/${studentId}`), studentData);

          if (i === 0) firstId = studentId;
          lastId = studentId;
          successCount.push(validStudents[i]);
        } catch (error) {
          importErrors.push({
            student: validStudents[i],
            error: error.message
          });
        }
      }

      setImportResults({
        success: successCount,
        errors: importErrors,
        totalProcessed: validStudents.length,
        firstId,
        lastId
      });

      // Clear the form if all imports were successful
      if (importErrors.length === 0) {
        setImportData([]);
        setValidationErrors([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } catch (error) {
      alert("Error importing students: " + error.message);
    }
  };

  const exportToExcel = () => {
    if (!activeCourse) {
      alert("Please select a course first");
      return;
    }

    // Get students data from the course
    const students = courses[activeCourse]?.students || {};

    // Convert students object to array
    const studentsArray = Object.values(students);

    if (studentsArray.length === 0) {
      alert("No students to export");
      return;
    }

    const studentsData = studentsArray.map(student => ({
      Name: student.name,
      Email: student.email,
      Phone: student.phone,
      "Parent Phone": student.parentPhone,
      StudentID: student.studentId
    }));

    const ws = XLSX.utils.json_to_sheet(studentsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Students");
    XLSX.writeFile(wb, `students_${activeCourse}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const downloadTemplate = () => {
    // Create template with sample data
    const templateData = [
      {
        Name: "John Doe",
        Email: "john.doe@example.com",
        Phone: "+1234567890",
        "Parent Phone": "+1234567891"
      },
      {
        Name: "Jane Smith",
        Email: "jane.smith@example.com",
        Phone: "+1234567892",
        "Parent Phone": "+1234567893"
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Students Template");
    XLSX.writeFile(wb, "students_import_template.xlsx");
  };

  const clearImportData = () => {
    setImportData([]);
    setValidationErrors([]);
    setImportResults(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Import/Export Students</h2>
        <button
          onClick={() => setIsImportExpanded(!isImportExpanded)}
          className="text-teal-600 hover:text-teal-800"
        >
          {isImportExpanded ? "▲ Hide Import" : "▼ Show Import"}
        </button>
      </div>

      <div className="flex space-x-4 mb-4">
        <button
          onClick={exportToExcel}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
        >
          Export to Excel
        </button>
        <button
          onClick={downloadTemplate}
          className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 transition-colors"
        >
          Download Template
        </button>
      </div>

      {isImportExpanded && (
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Excel File (Name and Email columns required)</label>
            <input
              type="file"
              accept=".xlsx, .xls"
              ref={fileInputRef}
              onChange={handleExcelUpload}
              className="mb-2 w-full"
            />
            <p className="text-xs text-gray-500">
              Required columns: Name, Email. Optional: Phone, Parent Phone
            </p>
          </div>

          {/* Validation Errors Display */}
          {validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <h3 className="text-red-800 font-semibold mb-2">
                Validation Errors ({validationErrors.length} errors found)
              </h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {validationErrors.map((error, index) => (
                  <div key={index} className="text-sm text-red-700">
                    <span className="font-medium">Row {error.rowIndex}:</span> {error.student.name || error.student.email || 'Unknown'}
                    <ul className="ml-4 list-disc">
                      {error.errors.map((err, errIndex) => (
                        <li key={errIndex}>{err}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Import Results Display */}
          {importResults && (
            <div className={`border rounded-md p-4 ${importResults.errors.length > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
              <h3 className={`font-semibold mb-2 ${importResults.errors.length > 0 ? 'text-yellow-800' : 'text-green-800'}`}>
                Import Results
              </h3>
              <p className="text-sm mb-2">
                Successfully imported: {importResults.success.length} students
                {importResults.firstId && importResults.lastId && (
                  <span> (IDs: {importResults.firstId}-{importResults.lastId})</span>
                )}
              </p>
              {importResults.errors.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-red-700 mb-2">
                    Failed to import {importResults.errors.length} students:
                  </p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {importResults.errors.map((error, index) => (
                      <div key={index} className="text-sm text-red-600">
                        <span className="font-medium">{error.student.name || error.student.email}:</span> {error.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {importData.length > 0 && (
            <div>
              <p className="text-sm mb-2">
                Found {importData.length} students to import
                {validationErrors.length > 0 && (
                  <span className="text-red-600"> ({validationErrors.length} with errors)</span>
                )}
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={clearImportData}
                  className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddImportedUsers}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex-1"
                  disabled={validationErrors.length > 0}
                >
                  Import Students
                </button>
              </div>
              {validationErrors.length > 0 && (
                <p className="text-xs text-red-600 mt-1">
                  Please fix validation errors before importing
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImportExportStudents;